import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  FaWallet,
  FaUsers,
  FaSearch,
  FaFilter,
  FaSort,
  FaEye,
  FaDownload,
  FaExclamationTriangle,
  FaCheckCircle,
  FaTimesCircle,
  FaMoneyBillWave,
  FaSync,
  FaEdit,
} from "react-icons/fa";
import { toast } from "react-toastify";
import axios from "axios";
import { API_URL } from "../../config/apiConfig";

const WalletDrivers = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState(
    searchParams.get("filter") || "all",
  );
  const [sortBy, setSortBy] = useState("balance");
  const [sortOrder, setSortOrder] = useState("desc");
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [showDriverModal, setShowDriverModal] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });

  const { user: adminUser } = useSelector((state) => state.auth);

  useEffect(() => {
    fetchDriverWallets();
  }, [filterStatus, sortBy, sortOrder, pagination.page]);

  const fetchDriverWallets = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("adminToken") || adminUser?.token;
      const config = {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page: pagination.page,
          limit: pagination.limit,
          filter: filterStatus,
          sortBy,
          sortOrder,
          search: searchTerm,
        },
      };

      let response;
      try {
        response = await axios.get(`${API_URL}/wallet/admin/drivers`, config);
      } catch (err) {
        if (err.response && err.response.status === 404) {
          response = await axios.get(`${API_URL}/admin/wallet/drivers`, config);
        } else {
          throw err;
        }
      }

      if (response.data.success) {
        const payload = response.data.data || {};
        const driversList = payload.drivers || [];
        const paginationInfo = payload.pagination || {};
        setDrivers(driversList);
        setPagination((prev) => ({
          ...prev,
          total: paginationInfo.total || 0,
          totalPages: paginationInfo.totalPages || 0,
        }));
      }
    } catch (error) {
      console.error("Error fetching driver wallets:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const getBalanceStatus = (balance) => {
    if (balance > 0)
      return { color: "green", text: "Positive", icon: FaCheckCircle };
    if (balance < 0)
      return { color: "red", text: "Negative", icon: FaExclamationTriangle };
    return { color: "gray", text: "Zero", icon: FaTimesCircle };
  };

  const getFilteredDrivers = () => {
    let filtered = drivers;

    if (searchTerm) {
      filtered = filtered.filter(
        (driver) =>
          driver.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          driver.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          driver.phone?.includes(searchTerm) ||
          driver.vehicleNumber
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()),
      );
    }

    return filtered;
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchDriverWallets();
  };

  const handleFilterChange = (newFilter) => {
    setFilterStatus(newFilter);
    setSearchParams(newFilter !== "all" ? { filter: newFilter } : {});
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const openDriverModal = (driver) => {
    setSelectedDriver(driver);
    setShowDriverModal(true);
  };

  const filteredDrivers = getFilteredDrivers();

  const stats = {
    total: pagination.total,
    positive: drivers.filter((d) => d.balance > 0).length,
    negative: drivers.filter((d) => d.balance < 0).length,
    zero: drivers.filter((d) => d.balance === 0).length,
    pendingWithdrawals: drivers.filter((d) => d.pendingWithdrawal?.amount > 0)
      .length,
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8">
        <div className="flex items-center mb-4 sm:mb-0">
          <FaUsers className="text-3xl text-blue-600 mr-3" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Driver Wallets</h1>
            <p className="text-gray-600 mt-1">
              Manage all driver wallet accounts and balances
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <button
            onClick={fetchDriverWallets}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <FaSync className="mr-2" />
            Refresh
          </button>

          <button className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
            <FaDownload className="mr-2" />
            Export
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Drivers</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <FaUsers className="text-2xl text-blue-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Positive Balance</p>
              <p className="text-2xl font-bold text-green-600">
                {stats.positive}
              </p>
            </div>
            <FaCheckCircle className="text-2xl text-green-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Negative Balance</p>
              <p className="text-2xl font-bold text-red-600">
                {stats.negative}
              </p>
            </div>
            <FaExclamationTriangle className="text-2xl text-red-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Zero Balance</p>
              <p className="text-2xl font-bold text-gray-600">{stats.zero}</p>
            </div>
            <FaTimesCircle className="text-2xl text-gray-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pending Withdrawals</p>
              <p className="text-2xl font-bold text-yellow-600">
                {stats.pendingWithdrawals}
              </p>
            </div>
            <FaMoneyBillWave className="text-2xl text-yellow-600" />
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6 border border-gray-200">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <FaSearch className="absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, email, phone, or vehicle number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </form>

          {/* Filter Buttons */}
          <div className="flex flex-wrap gap-2">
            {[
              { key: "all", label: "All Drivers", color: "gray" },
              { key: "positive", label: "Positive Balance", color: "green" },
              { key: "negative", label: "Negative Balance", color: "red" },
              { key: "zero", label: "Zero Balance", color: "gray" },
              {
                key: "withdrawal",
                label: "Pending Withdrawals",
                color: "yellow",
              },
            ].map((filter) => (
              <button
                key={filter.key}
                onClick={() => handleFilterChange(filter.key)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filterStatus === filter.key
                    ? `bg-${filter.color}-600 text-white`
                    : `bg-${filter.color}-100 text-${filter.color}-700 hover:bg-${filter.color}-200`
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Drivers Table */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Driver Wallet Details
          </h3>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort("name")}
                  >
                    <div className="flex items-center">
                      Driver
                      <FaSort className="ml-1" />
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort("balance")}
                  >
                    <div className="flex items-center">
                      Balance
                      <FaSort className="ml-1" />
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort("totalEarnings")}
                  >
                    <div className="flex items-center">
                      Total Earnings
                      <FaSort className="ml-1" />
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredDrivers.map((driver) => {
                  const balanceStatus = getBalanceStatus(driver.balance);
                  const BalanceIcon = balanceStatus.icon;

                  return (
                    <tr key={driver._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                              <span className="text-sm font-medium text-blue-600">
                                {driver.name?.charAt(0) || "D"}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {driver.name || "Unknown Driver"}
                            </div>
                            <div className="text-sm text-gray-500">
                              {driver.vehicleNumber || "No vehicle"}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {driver.email}
                        </div>
                        <div className="text-sm text-gray-500">
                          {driver.phone}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div
                          className={`flex items-center text-sm font-medium ${
                            driver.balance > 0
                              ? "text-green-600"
                              : driver.balance < 0
                                ? "text-red-600"
                                : "text-gray-600"
                          }`}
                        >
                          <BalanceIcon className="mr-1" />
                          {formatCurrency(driver.balance)}
                        </div>
                        {driver.pendingWithdrawal?.amount > 0 && (
                          <div className="text-xs text-yellow-600 mt-1">
                            Withdrawal pending:{" "}
                            {formatCurrency(driver.pendingWithdrawal.amount)}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-green-600">
                          {formatCurrency(
                            driver.totalEarnings ||
                            driver.totalOnlineEarnings ||
                            0
                          )}
                        </div>
                        <div className="text-xs text-red-600">
                          Commission:{" "}
                          {formatCurrency(
                            driver.totalCommissionPaid ||
                            driver.totalCommissionCut ||
                            0
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            driver.balance > 0
                              ? "bg-green-100 text-green-800"
                              : driver.balance < 0
                                ? "bg-red-100 text-red-800"
                                : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {balanceStatus.text}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => openDriverModal(driver)}
                            className="text-blue-600 hover:text-blue-900"
                            title="View Details"
                          >
                            <FaEye />
                          </button>
                          <button
                            className="text-green-600 hover:text-green-900"
                            title="Edit Wallet"
                          >
                            <FaEdit />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
                {Math.min(pagination.page * pagination.limit, pagination.total)}{" "}
                of {pagination.total} drivers
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() =>
                    setPagination((prev) => ({ ...prev, page: prev.page - 1 }))
                  }
                  disabled={pagination.page === 1}
                  className="px-3 py-1 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="px-3 py-1 bg-blue-600 text-white rounded-md text-sm font-medium">
                  {pagination.page}
                </span>
                <button
                  onClick={() =>
                    setPagination((prev) => ({ ...prev, page: prev.page + 1 }))
                  }
                  disabled={pagination.page === pagination.totalPages}
                  className="px-3 py-1 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Driver Details Modal */}
      {showDriverModal && selectedDriver && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">
                  Driver Wallet Details - {selectedDriver.name}
                </h3>
                <button
                  onClick={() => setShowDriverModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">
                    Driver Information
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="font-medium">Name:</span>{" "}
                      {selectedDriver.name}
                    </div>
                    <div>
                      <span className="font-medium">Email:</span>{" "}
                      {selectedDriver.email}
                    </div>
                    <div>
                      <span className="font-medium">Phone:</span>{" "}
                      {selectedDriver.phone}
                    </div>
                    <div>
                      <span className="font-medium">Vehicle:</span>{" "}
                      {selectedDriver.vehicleNumber}
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 mb-3">
                    Wallet Summary
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="font-medium">Current Balance:</span>
                      <span
                        className={`ml-2 font-bold ${selectedDriver.balance >= 0 ? "text-green-600" : "text-red-600"}`}
                      >
                        {formatCurrency(selectedDriver.balance)}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium">Total Earnings:</span>{" "}
                      {formatCurrency(selectedDriver.totalEarnings)}
                    </div>
                    <div>
                      <span className="font-medium">Commission Paid:</span>{" "}
                      {formatCurrency(selectedDriver.totalCommissionPaid)}
                    </div>
                    <div>
                      <span className="font-medium">Total Withdrawals:</span>{" "}
                      {formatCurrency(selectedDriver.totalWithdrawals)}
                    </div>
                  </div>
                </div>
              </div>

              {selectedDriver.pendingWithdrawal?.amount > 0 && (
                <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <h4 className="font-medium text-yellow-800 mb-2">
                    Pending Withdrawal
                  </h4>
                  <div className="text-sm text-yellow-700">
                    <div>
                      Amount:{" "}
                      {formatCurrency(selectedDriver.pendingWithdrawal.amount)}
                    </div>
                    <div>
                      Requested:{" "}
                      {new Date(
                        selectedDriver.pendingWithdrawal.requestDate,
                      ).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WalletDrivers;
